import React, {useState, useEffect, useMemo} from 'react';
import {useNavigate} from 'react-router';
import {
    Box,
    Typography,
    Container,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Button,
    Grid,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Avatar,
    Stack,
    Tabs,
    Tab,
    Card,
    CardContent
} from '@mui/material';
import {
    FilterList as FilterIcon,
    Refresh as RefreshIcon,
    Edit as EditIcon,
    ArrowBack as BackIcon,
    Clear as ClearIcon,
    FilterListOff as FilterListOffIcon,
    Add as AddIcon,
    School as CourseIcon,
    Person as StudentIcon,
    Topic as TopicIcon,
    Quiz as QuestionIcon,
    Visibility as ViewIcon,
    Delete as DeleteIcon,
    Group as EnrollmentIcon
} from '@mui/icons-material';
import {createApiClient} from '../../../services/ApiClient.jsx';
import CourseDetails from './CourseDetails.jsx';
import TopicManagement from './TopicManagement.jsx';

const CourseManagement = ({accessToken, onBack, standalone = false}) => {
    const navigate = useNavigate();

    // State management
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [teacherFilter, setTeacherFilter] = useState('');
    const [levelFilter, setLevelFilter] = useState('');

    // Create API client
    const apiClient = useMemo(() => {
        if (!accessToken) {
            console.error('No access token provided to CourseManagement');
            return null;
        }
        return createApiClient(accessToken);
    }, [accessToken]);

    // Check if any filters are active
    const hasActiveFilters = useMemo(() => {
        return searchTerm || teacherFilter || levelFilter;
    }, [searchTerm, teacherFilter, levelFilter]);

    // Fetch courses
    const fetchCourses = async (showLoading = true) => {
        if (!apiClient) {
            console.error('No API client available for fetchCourses');
            return;
        }

        try {
            if (showLoading) {
                setLoading(true);
            }
            setError(null);

            console.log('Fetching courses...');
            const response = await apiClient.get('/api/courses/', {
                skip: 0,
                limit: 100 // Maximum allowed by backend
            });
            console.log('Courses fetch response:', response);

            const courseData = response?.courses || response?.data?.courses || response || [];
            console.log('Processed course data:', courseData);
            setCourses(courseData);
        } catch (error) {
            console.error('Failed to fetch courses:', error);
            setError(error.message || 'Failed to load courses');
            setCourses([]);
        } finally {
            if (showLoading) {
                setLoading(false);
            }
        }
    };

    // Initial data load
    useEffect(() => {
        console.log('CourseManagement useEffect - apiClient:', apiClient);
        if (apiClient) {
            fetchCourses();
        }
    }, [apiClient]);

    // Handle back navigation
    const handleBackClick = () => {
        if (standalone) {
            navigate('/admin/dashboard');
        } else if (onBack) {
            onBack();
        }
    };

    // Handle pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Clear all filters
    const handleClearFilters = () => {
        setSearchTerm('');
        setTeacherFilter('');
        setLevelFilter('');
        setPage(0);
    };

    // Handle refresh
    const handleRefresh = async () => {
        setRefreshing(true);
        setPage(0);
        await fetchCourses();
        setRefreshing(false);
    };

    // Get unique teachers for filter
    const uniqueTeachers = useMemo(() => {
        const teachers = courses
            .filter(course => course.teacher && course.teacher.username)
            .map(course => ({
                id: course.teacher.id || course.teacher_id,
                username: course.teacher.username
            }));
        
        // Remove duplicates
        const unique = teachers.filter((teacher, index, self) => 
            index === self.findIndex(t => t.id === teacher.id)
        );
        
        return unique.sort((a, b) => a.username.localeCompare(b.username));
    }, [courses]);

    // Get unique academic levels for filter
    const uniqueLevels = useMemo(() => {
        const levels = [...new Set(courses.map(course => course.academic_level).filter(Boolean))];
        return levels.sort();
    }, [courses]);

    // Enhanced filtering
    const filteredCourses = useMemo(() => {
        let filtered = courses;

        // Text search
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(course =>
                course.title?.toLowerCase().includes(term) ||
                course.description?.toLowerCase().includes(term) ||
                course.teacher?.username?.toLowerCase().includes(term)
            );
        }

        // Teacher filter
        if (teacherFilter) {
            filtered = filtered.filter(course => 
                course.teacher_id === teacherFilter || course.teacher?.id === teacherFilter
            );
        }

        // Academic level filter
        if (levelFilter) {
            filtered = filtered.filter(course => course.academic_level === levelFilter);
        }

        return filtered;
    }, [courses, searchTerm, teacherFilter, levelFilter]);

    // Paginated courses
    const paginatedCourses = useMemo(() => {
        const startIndex = page * rowsPerPage;
        return filteredCourses.slice(startIndex, startIndex + rowsPerPage);
    }, [filteredCourses, page, rowsPerPage]);

    // Course statistics
    const courseStats = useMemo(() => {
        return {
            total: courses.length,
            totalStudents: courses.reduce((sum, course) => sum + (course.student_count || 0), 0),
            totalTopics: courses.reduce((sum, course) => sum + (course.topic_count || 0), 0),
            totalQuestions: courses.reduce((sum, course) => sum + (course.question_count || 0), 0)
        };
    }, [courses]);

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return 'N/A';
        try {
            return new Date(timestamp).toLocaleDateString();
        } catch {
            return 'N/A';
        }
    };

    // Handle view course details
    const handleViewCourse = (course) => {
        console.log('handleViewCourse called with:', course);
        setSelectedCourse(course);
        setActiveTab(0); // Details tab
        setDetailDialogOpen(true);
    };

    // Handle view course topics
    const handleViewTopics = (course) => {
        console.log('handleViewTopics called with:', course);
        setSelectedCourse(course);
        setActiveTab(1); // Topics tab
        setDetailDialogOpen(true);
    };

    // Handle course update
    const handleCourseUpdate = (updatedCourse) => {
        console.log('handleCourseUpdate called with:', updatedCourse);
        // Update the course in the courses array
        setCourses(prevCourses => 
            prevCourses.map(course => 
                course.id === updatedCourse.id ? updatedCourse : course
            )
        );
        // Update selected course
        setSelectedCourse(updatedCourse);
    };

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Handle course deletion
    const handleDeleteCourse = async (course) => {
        if (!apiClient || !course) return;
        
        if (!window.confirm(`Are you sure you want to delete "${course.title}"? This will delete all topics, forms, and enrolled students.`)) {
            return;
        }

        try {
            await apiClient.delete(`/api/courses/${course.id}`);
            
            // Remove course from list
            setCourses(prevCourses => prevCourses.filter(c => c.id !== course.id));
            
            // Close dialog if this course was selected
            if (selectedCourse?.id === course.id) {
                setDetailDialogOpen(false);
            }
        } catch (error) {
            console.error('Failed to delete course:', error);
            alert('Failed to delete course: ' + (error.message || 'Unknown error'));
        }
    };

    if (error) {
        return (
            <Box sx={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}>
                <Container maxWidth="xl" sx={{pt: 4, pb: 2}}>
                    <Alert severity="error" sx={{mb: 2}}>
                        {error}
                    </Alert>
                    <Button onClick={handleBackClick} startIcon={<BackIcon/>}>
                        Back to Dashboard
                    </Button>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{
            height: '87vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        }}>
            <Container maxWidth="xl" sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                pt: 4,
                pb: 2,
                overflow: 'hidden'
            }}>
                {/* Debug Info */}
                {!apiClient && (
                    <Alert severity="warning" sx={{mb: 2}}>
                        API Client not available. Access Token: {accessToken ? 'Available' : 'Missing'}
                    </Alert>
                )}

                {/* Header */}
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexShrink: 0}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                        <IconButton onClick={handleBackClick}>
                            <BackIcon/>
                        </IconButton>
                        <Typography variant="h4" component="h1">
                            Course Management
                        </Typography>
                        {hasActiveFilters && (
                            <Chip
                                label="Filtered"
                                color="primary"
                                size="small"
                                variant="outlined"
                            />
                        )}
                    </Box>
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            startIcon={refreshing ? <CircularProgress size={16}/> : <RefreshIcon/>}
                            onClick={handleRefresh}
                            disabled={loading || refreshing}
                        >
                            Refresh
                        </Button>
                    </Stack>
                </Box>

                {/* Statistics Cards */}
                <Grid container spacing={2} sx={{mb: 3, flexShrink: 0}}>
                    <Grid size={{xs: 12, sm: 6, md: 3}}>
                        <Card>
                            <CardContent>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                    <Avatar sx={{bgcolor: 'primary.main'}}>
                                        <CourseIcon/>
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h4">{courseStats.total}</Typography>
                                        <Typography variant="body2" color="text.secondary">Total Courses</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{xs: 12, sm: 6, md: 3}}>
                        <Card>
                            <CardContent>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                    <Avatar sx={{bgcolor: 'success.main'}}>
                                        <StudentIcon/>
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h4">{courseStats.totalStudents}</Typography>
                                        <Typography variant="body2" color="text.secondary">Total Students</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{xs: 12, sm: 6, md: 3}}>
                        <Card>
                            <CardContent>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                    <Avatar sx={{bgcolor: 'info.main'}}>
                                        <TopicIcon/>
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h4">{courseStats.totalTopics}</Typography>
                                        <Typography variant="body2" color="text.secondary">Total Topics</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid size={{xs: 12, sm: 6, md: 3}}>
                        <Card>
                            <CardContent>
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                                    <Avatar sx={{bgcolor: 'warning.main'}}>
                                        <QuestionIcon/>
                                    </Avatar>
                                    <Box>
                                        <Typography variant="h4">{courseStats.totalQuestions}</Typography>
                                        <Typography variant="body2" color="text.secondary">Total Questions</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Filters */}
                <Paper sx={{p: 3, mb: 3, flexShrink: 0}}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                        <Typography variant="h6">
                            <FilterIcon sx={{mr: 1, verticalAlign: 'middle'}}/>
                            Filters
                        </Typography>
                        {hasActiveFilters && (
                            <Button
                                size="small"
                                startIcon={<FilterListOffIcon/>}
                                onClick={handleClearFilters}
                            >
                                Clear All
                            </Button>
                        )}
                    </Box>

                    <Grid container spacing={2} alignItems="center">
                        <Grid size={{xs: 12, md: 4}}>
                            <TextField
                                fullWidth
                                label="Search Courses"
                                placeholder="Title, description, or teacher name"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    endAdornment: searchTerm && (
                                        <IconButton
                                            size="small"
                                            onClick={() => setSearchTerm('')}
                                        >
                                            <ClearIcon/>
                                        </IconButton>
                                    )
                                }}
                            />
                        </Grid>

                        <Grid size={{xs: 12, md: 4}}>
                            <FormControl fullWidth>
                                <InputLabel>Teacher</InputLabel>
                                <Select
                                    value={teacherFilter}
                                    label="Teacher"
                                    onChange={(e) => setTeacherFilter(e.target.value)}
                                >
                                    <MenuItem value="">All Teachers</MenuItem>
                                    {uniqueTeachers.map((teacher) => (
                                        <MenuItem key={teacher.id} value={teacher.id}>
                                            {teacher.username}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid size={{xs: 12, md: 4}}>
                            <FormControl fullWidth>
                                <InputLabel>Academic Level</InputLabel>
                                <Select
                                    value={levelFilter}
                                    label="Academic Level"
                                    onChange={(e) => setLevelFilter(e.target.value)}
                                >
                                    <MenuItem value="">All Levels</MenuItem>
                                    {uniqueLevels.map((level) => (
                                        <MenuItem key={level} value={level}>
                                            {level}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Results Info */}
                <Box sx={{mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0}}>
                    <Typography variant="body2" color="text.secondary">
                        {loading ? 'Loading...' : (
                            `Showing ${paginatedCourses.length} of ${filteredCourses.length} courses (Total: ${courses.length})`
                        )}
                        {hasActiveFilters && ` [Filters active]`}
                    </Typography>
                </Box>

                {/* Courses Table */}
                <Paper sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <TableContainer sx={{
                        flex: 1,
                        overflow: 'auto'
                    }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Course</TableCell>
                                    <TableCell>Teacher</TableCell>
                                    <TableCell>Level</TableCell>
                                    <TableCell>Students</TableCell>
                                    <TableCell>Topics</TableCell>
                                    <TableCell>Questions</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{py: 4}}>
                                            <CircularProgress/>
                                            <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                                                Loading courses...
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : paginatedCourses.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{py: 4}}>
                                            <Typography variant="body1" color="text.secondary">
                                                {courses.length === 0 ? 'No courses found' : 'No courses match your filters'}
                                            </Typography>
                                            {hasActiveFilters && (
                                                <Button
                                                    size="small"
                                                    onClick={handleClearFilters}
                                                    sx={{mt: 1}}
                                                >
                                                    Clear filters
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    paginatedCourses.map((course) => (
                                        <TableRow key={course.id} hover>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" fontWeight="bold">
                                                        {course.title}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {course.description?.substring(0, 60)}{course.description?.length > 60 ? '...' : ''}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                                    <Avatar sx={{width: 24, height: 24}}>
                                                        {course.teacher?.username?.charAt(0)?.toUpperCase() || 'T'}
                                                    </Avatar>
                                                    <Typography variant="body2">
                                                        {course.teacher?.username || 'Unknown'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip 
                                                    label={course.academic_level} 
                                                    size="small" 
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell align="center">{course.student_count}</TableCell>
                                            <TableCell align="center">{course.topic_count}</TableCell>
                                            <TableCell align="center">{course.question_count}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatTimestamp(course.created_at)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5}>
                                                    <Tooltip title="View Details">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleViewCourse(course)}
                                                        >
                                                            <ViewIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Manage Topics">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleViewTopics(course)}
                                                        >
                                                            <TopicIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete Course">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteCourse(course)}
                                                            color="error"
                                                        >
                                                            <DeleteIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        component="div"
                        count={filteredCourses.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{flexShrink: 0}}
                    />
                </Paper>
            </Container>

            {/* Course Details Dialog */}
            <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>
                        <Avatar sx={{width: 40, height: 40, bgcolor: 'primary.main'}}>
                            <CourseIcon/>
                        </Avatar>
                        <Box>
                            <Typography variant="h6">
                                {selectedCourse?.title || 'Course Details'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {selectedCourse?.description}
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>

                <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                    <Tabs value={activeTab} onChange={handleTabChange} aria-label="course dialog tabs">
                        <Tab 
                            label="Course Details" 
                            icon={<EditIcon />}
                            iconPosition="start"
                        />
                        <Tab 
                            label="Topic Management" 
                            icon={<TopicIcon />}
                            iconPosition="start"
                        />
                    </Tabs>
                </Box>

                <DialogContent sx={{height: '70vh', overflow: 'hidden', p: 0}}>
                    {/* Course Details Tab */}
                    {activeTab === 0 && selectedCourse && (
                        <CourseDetails
                            selectedCourse={selectedCourse}
                            onCourseUpdate={handleCourseUpdate}
                            apiClient={apiClient}
                        />
                    )}

                    {/* Topic Management Tab */}
                    {activeTab === 1 && selectedCourse && (
                        <TopicManagement
                            selectedCourse={selectedCourse}
                            apiClient={apiClient}
                        />
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CourseManagement;